import { getConfig, translator } from '@vantiva-sa/homesight_humaui/Store/configStore';
import { ConfigSourceKeys } from '@vantiva-sa/homesight_humaui/Utilities/Constants';
import {
  capitalize,
  get,
  isArray,
  isEmpty,
  isString,
  lowerCase,
  merge,
  startCase,
  toUpper,
  uniq,
} from 'lodash';
import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { getSupportedConditionTypesForCategory } from '../Dashboard/Rules/RulesUtils';

const preferredConditionTypeOrder = [
  'indoor_temperature',
  'motion',
  'contact_sensor',
  'occupancy',
  'toilet_flush',
];

const toOptionValue = (rawValue) => {
  if (typeof rawValue === 'number' || typeof rawValue === 'boolean') {
    return String(rawValue);
  }
  return rawValue || '';
};

const getThresholds = (templates) =>
  templates.map((template) => get(template, 'threshold', null)).filter(Boolean);

const isNumericThreshold = (threshold) => typeof threshold === 'number';

const isNumericRangeThreshold = (threshold) =>
  threshold &&
  typeof threshold === 'object' &&
  threshold.type === 'numeric' &&
  typeof threshold.min === 'number' &&
  typeof threshold.max === 'number';

const getNumericThreshold = (templates) => getThresholds(templates).find(isNumericThreshold);

const getNumericRangeThreshold = (templates) =>
  getThresholds(templates).find(isNumericRangeThreshold);

const buildOperatorOptions = (templates, trans) => {
  const defaultOperators = [{ value: 'equals', label: trans('RULEBUILDER_OPERATOR_EQUALS') }];
  if (isEmpty(templates)) return defaultOperators;

  const operators = uniq(
    templates
      .map((template) => template.operator)
      .filter((operator) => isString(operator) && operator !== '')
  ).map((operator) => ({
    value: operator,
    label: trans(`RULEBUILDER_OPERATOR_${toUpper(operator)}`) || capitalize(lowerCase(operator)),
  }));

  return isEmpty(operators) ? defaultOperators : operators;
};

const buildSelectValueOptions = (templates, trans) =>
  uniq(
    templates
      .map((template) => toOptionValue(template.threshold))
      .filter((value) => isString(value) && value !== '')
  ).map((value) => ({
    value,
    label: trans(`RULEBUILDER_VALUE_${toUpper(value)}`) || startCase(value),
  }));

const getNumericDefaultValue = (templates) => {
  const numericRangeThreshold = getNumericRangeThreshold(templates);
  if (numericRangeThreshold)
    return Math.round((numericRangeThreshold.min + numericRangeThreshold.max) / 2);

  const numericValueThreshold = getNumericThreshold(templates);
  if (typeof numericValueThreshold === 'number') return numericValueThreshold;

  return 0;
};

const buildDeviceOptions = (conditionTypeConfig) => {
  const deviceTypes = isArray(conditionTypeConfig.devicetypes)
    ? conditionTypeConfig.devicetypes
    : [];

  return deviceTypes.map((deviceType) => ({ value: deviceType, label: startCase(deviceType) }));
};

// Reads the units array from the first numeric threshold found in templates.
const getSchemaUnits = (templates) => {
  const thresholds = getThresholds(templates);
  const threshold = thresholds.find(
    (t) => t && typeof t === 'object' && t.type === 'numeric' && isArray(t.units)
  );
  return get(threshold, 'units', []);
};

/**
 * Factory function to create condition types and related options from insight definitions
 * @param {Object} insightDefinitions - The insight definitions schema
 * @param {Array} supportedCategories - List of categories supported for rules, used to filter the categories from schema
 * @param {Array} supportedConditionTypes - List of condition types supported for rules, used to filter the condition types from schema
 * @returns {Object} Object containing conditionTypes, categoryOptions, conditionTypeOptions, conditionTypeOrder
 */
const buildConditionMetadata = (
  insightDefinitions,
  supportedCategories,
  supportedConditionTypes,
  trans
) => {
  let conditionTypes = {};
  let categoryOptions = [];
  let conditionTypeOptions = [];
  let conditionTypeOrder = [];

  if (isEmpty(insightDefinitions)) {
    return { conditionTypes, categoryOptions, conditionTypeOptions, conditionTypeOrder };
  }

  conditionTypes = (insightDefinitions?.condition_types || []).reduce(
    (accumulator, conditionTypeConfig) => {
      const conditionTypeId = conditionTypeConfig?.id;
      if (!conditionTypeId) return accumulator;

      if (!supportedConditionTypes.includes(conditionTypeId)) return accumulator;

      const templates = conditionTypeConfig?.condition_templates;
      if (!isArray(templates) || isEmpty(templates)) return accumulator;

      const thresholds = getThresholds(templates);
      const hasNumericThreshold = thresholds.some(isNumericThreshold);
      const hasNumericRangeThreshold = thresholds.some(isNumericRangeThreshold);
      const operators = buildOperatorOptions(templates, trans);
      const values =
        hasNumericThreshold || hasNumericRangeThreshold
          ? []
          : buildSelectValueOptions(templates, trans);

      accumulator[conditionTypeId] = merge(
        {},
        {
          label: conditionTypeConfig.name || startCase(conditionTypeId),
          devices: buildDeviceOptions(conditionTypeConfig),
          operators,
          defaultOperator: operators[0]?.value,
        },
        hasNumericThreshold || hasNumericRangeThreshold
          ? {
              valueType: 'number',
              defaultValue: getNumericDefaultValue(templates),
              units: getSchemaUnits(templates),
            }
          : { valueType: 'select', values, defaultValue: values[0]?.value || '' },
        hasNumericRangeThreshold ? { valueRanges: getNumericRangeThreshold(templates) } : {}
      );

      return accumulator;
    },
    {}
  );

  // Filter and map categories to options for select input
  categoryOptions = (insightDefinitions?.categories || [])
    .filter((c) => !isEmpty(supportedCategories) && supportedCategories.includes(c.key))
    .map((c) => ({
      value: c.key,
      label: trans(`RULEBUILDER_CATEGORY_${toUpper(c.key)}`) || startCase(c.key),
    }));

  const mappedConditionTypeOrder = isEmpty(conditionTypes) ? [] : Object.keys(conditionTypes);

  conditionTypeOrder = [
    ...preferredConditionTypeOrder.filter((typeKey) => mappedConditionTypeOrder.includes(typeKey)),
    ...mappedConditionTypeOrder.filter((typeKey) => !preferredConditionTypeOrder.includes(typeKey)),
  ];

  conditionTypeOptions = conditionTypeOrder.map((typeKey) => ({
    value: typeKey,
    label:
      trans(`RULEBUILDER_TYPE_${toUpper(typeKey)}`) ||
      get(conditionTypes, [typeKey, 'label'], typeKey),
  }));

  return { conditionTypes, categoryOptions, conditionTypeOptions, conditionTypeOrder };
};

/**
 * Custom hook to get condition metadata from insight definitions stored in Redux
 * @param {Array} supportedCategories - List of categories supported for rules, used to filter the categories from schema
 * @returns {Object} Object containing conditionTypes, categoryOptions, conditionTypeOptions, conditionTypeOrder
 */
const useInsightRuleSchemaMetadata = ({ supportedCategories = [] }) => {
  const trans = useSelector(translator);
  const insightDefinitions = useSelector(getConfig(ConfigSourceKeys.insight_rules_schema));
  const supportedConditionTypes = useMemo(
    () => getSupportedConditionTypesForCategory(supportedCategories),
    [supportedCategories]
  );
  const metadata = useMemo(
    () =>
      buildConditionMetadata(
        insightDefinitions,
        supportedCategories,
        supportedConditionTypes,
        trans
      ),
    [insightDefinitions, supportedCategories, supportedConditionTypes, trans]
  );
  return metadata;
};

export default useInsightRuleSchemaMetadata;
